import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { useCycle, useCycleMutations } from "../api/hooks";
import type { CycleDay, CycleSettings } from "../api/types";
import { PHASE_LABEL, estimateCycle, phaseOfDay, type CycleEstimate, type Phase } from "../lib/cycle";
import { MONTHS_SHORT, WEEKDAYS_SHORT, addDays, diffDays, parts, weekday, type DateStr } from "../lib/date";
import { useUi } from "../state/ui";
import { PageHead } from "./TasksPage";
import "./pages.css";
import "./life.css";

const BASE_SYMPTOMS = ["Cólica", "Oscilação de humor", "Inchaço", "Dor de cabeça", "Cansaço", "Sensibilidade nos seios", "Acne", "Fluxo intenso"];

/** "qui., 16 de out." */
function dayLabel(d: DateStr): string {
  const { m, d: day } = parts(d);
  return `${WEEKDAYS_SHORT[weekday(d)]}, ${day} de ${MONTHS_SHORT[m - 1]}`;
}

/** "16 de out." */
function shortLabel(d: DateStr): string {
  const { m, d: day } = parts(d);
  return `${day} de ${MONTHS_SHORT[m - 1]}`;
}

function inDays(from: DateStr, to: DateStr): string {
  const n = diffDays(from, to);
  if (n === 0) return "hoje";
  if (n === 1) return "amanhã";
  if (n === -1) return "ontem";
  return n > 0 ? `em ${n} dias` : `há ${-n} dias`;
}

// --- Wheel -------------------------------------------------------------------------------------

const CX = 140;
const CY = 140;
const R_OUT = 124;
const R_IN = 86;

function polar(r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [CX + r * Math.cos(rad), CY + r * Math.sin(rad)];
}

function arc(startDeg: number, endDeg: number): string {
  const large = endDeg - startDeg > 180 ? 1 : 0;
  const [x1, y1] = polar(R_OUT, startDeg);
  const [x2, y2] = polar(R_OUT, endDeg);
  const [x3, y3] = polar(R_IN, endDeg);
  const [x4, y4] = polar(R_IN, startDeg);
  const f = (n: number) => n.toFixed(2);
  return `M${f(x1)},${f(y1)} A${R_OUT},${R_OUT} 0 ${large} 1 ${f(x2)},${f(y2)} L${f(x3)},${f(y3)} A${R_IN},${R_IN} 0 ${large} 0 ${f(x4)},${f(y4)} Z`;
}

/**
 * One segment per day of the current cycle, colored by estimated phase; days logged with
 * bleeding show as menstruation with a dot; days ahead are faded; the selected day is ringed.
 */
function CycleWheel({ e, today, days, selected, onSelect }: { e: CycleEstimate; today: DateStr; days: Map<DateStr, CycleDay>; selected: DateStr; onSelect: (d: DateStr) => void }) {
  const count = Math.max(e.length, e.day);
  const seg = 360 / count;
  const gap = Math.min(1.6, seg * 0.16);
  return (
    <svg className="wheel" viewBox="0 0 280 280" role="group" aria-label="Dias do ciclo atual, coloridos pela fase estimada">
      {Array.from({ length: count }, (_, i) => {
        const n = i + 1;
        const date = addDays(e.start, i);
        const logged = days.get(date);
        const phase: Phase = logged?.flow ? "menstruacao" : n > e.length ? "lutea" : phaseOfDay(n, e);
        const start = i * seg + gap / 2;
        const end = (i + 1) * seg - gap / 2;
        const [tx, ty] = polar((R_OUT + R_IN) / 2, (start + end) / 2);
        const cls = [`wheel-seg ph-${phase}`, date > today && "future", date === selected && "selected", date === today && "today"].filter(Boolean).join(" ");
        return (
          <g key={date} className={cls} onClick={() => onSelect(date)}>
            <path d={arc(start, end)}>
              <title>
                {dayLabel(date)} · dia {n}
                {logged?.flow ? " · menstruação registrada" : ""}
              </title>
            </path>
            {count <= 40 && (
              <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central" className="wheel-num">
                {n}
              </text>
            )}
            {(logged?.flow || (logged?.symptoms.length ?? 0) > 0) && <circle cx={polar(R_IN - 9, (start + end) / 2)[0]} cy={polar(R_IN - 9, (start + end) / 2)[1]} r={2.4} className={`wheel-dot${logged?.flow ? " flow" : ""}`} />}
          </g>
        );
      })}
      <text x={CX} y={CY - 8} textAnchor="middle" className="wheel-day">
        Dia {e.day}
      </text>
      <text x={CX} y={CY + 18} textAnchor="middle" className="wheel-caption">
        {e.lateDays ? `${e.lateDays} ${e.lateDays === 1 ? "dia" : "dias"} de atraso` : PHASE_LABEL[e.phase]}
      </text>
    </svg>
  );
}

// --- Page --------------------------------------------------------------------------------------

function NumberField({ label, value, min, max, onCommit }: { label: string; value: number; min: number; max: number; onCommit: (n: number) => void }) {
  const [v, setV] = useState(String(value));
  useEffect(() => setV(String(value)), [value]);
  const commit = () => {
    const n = Number(v);
    if (Number.isInteger(n) && n >= min && n <= max) {
      if (n !== value) onCommit(n);
    } else setV(String(value));
  };
  return (
    <label className="life-field">
      <span>{label}</span>
      <input className="field filled tabular" inputMode="numeric" value={v} onChange={(e) => setV(e.target.value.replace(/\D/g, ""))} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && commit()} />
    </label>
  );
}

function Settings({ s, onChange }: { s: CycleSettings; onChange: (c: Partial<CycleSettings>) => void }) {
  return (
    <div className="life-form cycle-settings">
      <label className="life-field">
        <span>Início da última menstruação</span>
        <input className="field filled" type="date" value={s.lastStart ?? ""} onChange={(e) => onChange({ lastStart: e.target.value || null })} />
      </label>
      <NumberField label="Duração média do ciclo (dias)" value={s.cycleLength} min={15} max={60} onCommit={(n) => onChange({ cycleLength: n })} />
      <NumberField label="Duração da menstruação (dias)" value={s.periodLength} min={1} max={15} onCommit={(n) => onChange({ periodLength: n })} />
    </div>
  );
}

const PHASES: Array<{ phase: Phase; title: string; text: string }> = [
  {
    phase: "menstruacao",
    title: "Menstruação",
    text: "Do dia 1 ao fim do sangramento (em média 3 a 7 dias). Cólica, cansaço e sensibilidade são mais comuns aqui."
  },
  {
    phase: "folicular",
    title: "Fase folicular",
    text: "Começa com a menstruação e vai até a ovulação. O estrogênio sobe aos poucos e a energia costuma melhorar no fim dela."
  },
  {
    phase: "ovulacao",
    title: "Ovulação e janela fértil",
    text: "A ovulação cai, em média, 14 dias antes da próxima menstruação. A janela fértil é a ovulação e os ~5 dias antes dela."
  },
  {
    phase: "lutea",
    title: "Fase lútea",
    text: "Da ovulação até a próxima menstruação, com duração mais constante (~14 dias). É quando aparecem os sintomas de TPM, se vierem."
  }
];

/** "Ciclo menstrual": an estimate from the last start and the average lengths, plus days logged by hand. */
export function CyclePage() {
  const ui = useUi();
  const today = ui.today;
  // Window of logged days: the current cycle and the one before, with room for long cycles.
  const from = addDays(today, -120);
  const to = addDays(today, 60);
  const { data, isLoading } = useCycle(from, to);
  const m = useCycleMutations(from, to);
  const [selected, setSelected] = useState(today);
  const [newSymptom, setNewSymptom] = useState("");
  const fail = (err: unknown) => ui.showToast(`Não foi possível salvar: ${(err as Error).message}`);

  const settings = data?.settings;
  const days = useMemo(() => new Map((data?.days ?? []).map((d) => [d.day, d])), [data]);
  const estimate = settings?.lastStart ? estimateCycle(settings.lastStart, settings.cycleLength, settings.periodLength, today) : null;
  const sel = days.get(selected) ?? { day: selected, flow: false, symptoms: [] };
  // Symptoms logged on the selected day stay visible even if no longer in the lists.
  const symptoms = [...new Set([...BASE_SYMPTOMS, ...(settings?.customSymptoms ?? []), ...sel.symptoms])];

  const setSettings = (c: Partial<CycleSettings>) => m.settings.mutate(c, { onError: fail });

  const setFlow = (day: DateStr, flow: boolean) => {
    m.day.mutate({ day, flow }, { onError: fail });
    // Bleeding logged after the expected end of this period, with none the day before: a new cycle started.
    if (flow && settings && !days.get(addDays(day, -1))?.flow && (!settings.lastStart || diffDays(settings.lastStart, day) > settings.periodLength + 7)) {
      setSettings({ lastStart: day });
      ui.showToast(`Início do ciclo atualizado para ${shortLabel(day)}`);
    }
  };

  const startToday = () => {
    m.day.mutate({ day: today, flow: true }, { onError: fail });
    setSettings({ lastStart: today });
    setSelected(today);
  };

  const toggleSymptom = (name: string) => {
    const next = sel.symptoms.includes(name) ? sel.symptoms.filter((s) => s !== name) : [...sel.symptoms, name];
    m.day.mutate({ day: selected, symptoms: next }, { onError: fail });
  };

  const addSymptom = () => {
    const name = newSymptom.trim().replace(/\s+/g, " ");
    if (!name || !settings) return;
    setNewSymptom("");
    if (!symptoms.some((s) => s.toLowerCase() === name.toLowerCase())) setSettings({ customSymptoms: [...settings.customSymptoms, name] });
    if (!sel.symptoms.includes(name)) m.day.mutate({ day: selected, symptoms: [...sel.symptoms, name] }, { onError: fail });
  };

  const removeCustom = (name: string) => settings && setSettings({ customSymptoms: settings.customSymptoms.filter((s) => s !== name) });

  if (isLoading || !settings) {
    return (
      <div className="center-fill">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page-scroll">
      <section className="tile">
        <div className="page">
          <PageHead label="Ciclo menstrual" title="Seu corpo, seu ritmo.">
            Uma estimativa a partir da última menstruação e da duração média do ciclo. Também pelo WhatsApp: “menstruei hoje”, “estou com cólica”.
          </PageHead>

          {estimate ? (
            <div className="cycle-hero">
              <CycleWheel e={estimate} today={today} days={days} selected={selected} onSelect={setSelected} />
              <div className="cycle-facts">
                <span className={`phase-chip ph-${estimate.phase}`}>{estimate.lateDays ? "Menstruação atrasada" : PHASE_LABEL[estimate.phase]}</span>
                <h2 className="cycle-day display">Dia {estimate.day} do ciclo</h2>
                <dl className="cycle-list">
                  <div>
                    <dt>Próxima menstruação</dt>
                    <dd>
                      {shortLabel(estimate.nextPeriod)} <span>{estimate.lateDays ? `prevista ${inDays(today, estimate.nextPeriod)}` : inDays(today, estimate.nextPeriod)}</span>
                    </dd>
                  </div>
                  <div>
                    <dt>Ovulação estimada</dt>
                    <dd>
                      {shortLabel(estimate.ovulation)} <span>{inDays(today, estimate.ovulation)}</span>
                    </dd>
                  </div>
                  <div>
                    <dt>Janela fértil estimada</dt>
                    <dd>
                      {shortLabel(estimate.fertileStart)} – {shortLabel(estimate.fertileEnd)}
                    </dd>
                  </div>
                </dl>
                {!days.get(today)?.flow && (
                  <button className="btn primary" onClick={startToday}>
                    Menstruação começou hoje
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="card cycle-empty">
              <h2 className="col-title display">Comece pela última menstruação.</h2>
              <p className="page-lead">Informe abaixo o dia em que ela começou — ou toque no botão se for hoje.</p>
              <button className="btn primary" onClick={startToday}>
                Menstruação começou hoje
              </button>
            </div>
          )}

          <section className="row-section">
            <h2 className="row-section-title eyebrow">Dados do ciclo</h2>
            <Settings s={settings} onChange={setSettings} />
          </section>

          {estimate && (
            <section className="row-section">
              <h2 className="row-section-title eyebrow day-head">
                {selected === today ? "Hoje" : dayLabel(selected)}
                {selected !== today && (
                  <button className="btn text" onClick={() => setSelected(today)}>
                    Voltar para hoje
                  </button>
                )}
              </h2>
              <p className="life-hint">Toque em um dia da roda para registrar outro dia.</p>
              <label className="checkbox cycle-flow">
                <input type="checkbox" checked={sel.flow} onChange={(e) => setFlow(selected, e.target.checked)} />
                Menstruação neste dia
              </label>
              <div className="symptoms" role="group" aria-label="Sintomas">
                {symptoms.map((name) => {
                  const on = sel.symptoms.includes(name);
                  const custom = !BASE_SYMPTOMS.includes(name);
                  return (
                    <span key={name} className={`pill symptom${on ? " on" : ""}`}>
                      <button type="button" aria-pressed={on} onClick={() => toggleSymptom(name)}>
                        {name}
                      </button>
                      {custom && !on && (
                        <button type="button" className="symptom-x" aria-label={`Remover ${name} da lista`} onClick={() => removeCustom(name)}>
                          <X size={12} />
                        </button>
                      )}
                    </span>
                  );
                })}
                <form
                  className="symptom-add"
                  onSubmit={(e) => {
                    e.preventDefault();
                    addSymptom();
                  }}
                >
                  <input className="pill" value={newSymptom} maxLength={40} onChange={(e) => setNewSymptom(e.target.value)} placeholder="Outro sintoma…" aria-label="Adicionar sintoma" />
                </form>
              </div>
            </section>
          )}
        </div>
      </section>

      <section className="tile tile-parchment">
        <div className="page">
          <header className="col-head">
            <h2 className="col-title display">As 4 fases do ciclo</h2>
          </header>
          <div className="phase-grid">
            {PHASES.map((p) => (
              <article key={p.phase} className={`card phase-card ph-${p.phase}`}>
                <h3>{p.title}</h3>
                <p>{p.text}</p>
              </article>
            ))}
          </div>
          <p className="life-note">
            <strong>É uma estimativa, não um método exato.</strong> O corpo varia de um mês para o outro, principalmente a ovulação. Use como referência — não como método contraceptivo nem diagnóstico.
          </p>
        </div>
      </section>
    </div>
  );
}
